import {
  Button,
  TabMenu,
  TabMenuContent,
  TabMenuList,
  TabMenuTrigger,
} from '@ads/components-react';
import { Cog6ToothIcon, SparklesIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest } from '@/shared/api/generated/types';
import type { QuestionDetail } from '../../../hooks/useQuestionEdit';
import { buildAiReviewRequest, normalizeHtml } from '../../../utils';
import { type QuestionAiReviewCurrent, QuestionAiReviewModal } from '../QuestionAiReviewModal';
import { QuestionPreviewPanel } from '../QuestionPreviewPanel';
import { DependencyTab } from './parts/DependencyTab';
import { GeneralTab } from './parts/GeneralTab';
import { useQuestionEditForm } from './useQuestionEditForm';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do QuestionEditForm */
export interface QuestionEditFormProps {
  /** Dados completos da questão */
  readonly question: QuestionDetail;
  /** Modo de operação: criação (usa POST) ou edição (usa PUT) */
  readonly mode?: 'create' | 'edit';
  /** Callback ao salvar o formulário */
  readonly onSave: (data: Partial<QuestionDetail>) => void;
  /** Callback ao cancelar */
  readonly onCancel: () => void;
  /** Estado de salvamento */
  readonly isSaving: boolean;
  /** Notifica o pai sempre que texto, alternativas ou gabarito mudam (para preview live) */
  readonly onPreviewChange?: (data: {
    text: string;
    alternatives: QuestionDetail['alternatives'];
    correctId: string;
  }) => void;
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Formulário de criação/edição de questão.
 *
 * Card de configurações com abas "Geral" e "Dependência" (campos agrupados em
 * seções), pré-visualização ao vivo sempre visível abaixo do formulário e uma
 * barra de ações fixa (Revisar com IA · Cancelar · Salvar).
 *
 * @example
 * ```tsx
 * <QuestionEditForm
 *   question={questionDetail}
 *   onSave={save}
 *   onCancel={() => navigate({ to: '/question-library' })}
 *   isSaving={isSaving}
 * />
 * ```
 */
export function QuestionEditForm({
  question,
  onSave,
  onCancel,
  isSaving,
  onPreviewChange,
}: QuestionEditFormProps) {
  const { t } = useTranslation('questionLibrary');
  const state = useQuestionEditForm({
    question,
    onSave,
    ...(onPreviewChange ? { onPreviewChange } : {}),
  });

  const {
    form,
    componentTypeId,
    isChoiceType,
    subtypeId,
    hasModelAnswer,
    hasKeyTerms,
    alternatives,
    correctAlternativeIds,
    currentLabel,
    applyAiReview,
    handleSave,
  } = state;

  const [reviewData, setReviewData] = useState<{
    request: PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest;
    current: QuestionAiReviewCurrent;
  } | null>(null);
  // Controla a abertura via prop `open` (não desmontando o modal ao fechar): o
  // Radix Dialog só restaura `pointer-events` do body na transição open→false.
  // Desmontar enquanto ainda aberto deixaria a página inteira sem cliques.
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const hasLabelContent = normalizeHtml(currentLabel).length > 0;

  function handleOpenReview() {
    // Os editores rich text (enunciado, informação) só propagam o valor ao
    // formulário no `onBlur`. Se o usuário edita e clica em "Revisar com IA" sem
    // que o editor perca o foco, `form.getValues()` ainda traz o valor antigo.
    // Forçamos o blur do elemento focado — o commit do Jodit é síncrono — para
    // garantir que o payload reflita a edição atual.
    (document.activeElement as HTMLElement | null)?.blur();

    const values = form.getValues();

    // A marcação de alternativa correta vive em `correctAlternativeIds` (fonte de
    // verdade); o `isCorrect` do array `alternatives` reflete apenas o estado
    // inicial da questão. Derivamos as alternativas com o `isCorrect` atualizado
    // para que tanto o payload quanto a coluna "Sua versão" do modal usem a
    // seleção corrente — e não a alternativa correta original.
    const currentAlternatives = alternatives.map(alt => ({
      ...alt,
      isCorrect: correctAlternativeIds.includes(alt.id),
    }));

    // Campos do subtipo dissertativo: só entram no payload quando o subtipo
    // corrente os aceita — é o que define a estratégia de revisão da IA.
    const modelAnswer = hasModelAnswer
      ? ((values.modelAnswer as string | null | undefined) ?? '')
      : '';
    const keyTerms = hasKeyTerms ? ((values.keyTerms as string | null | undefined) ?? '') : '';

    const request = buildAiReviewRequest({
      componentType: componentTypeId,
      cultureInfo: values.cultureInfo,
      label: values.label,
      information: values.information ?? '',
      personAuthorId: values.personAuthorId,
      weight: values.weight,
      level: values.level,
      disciplineId: values.disciplineId,
      companyId: values.companyId,
      isChoiceType,
      alternatives: currentAlternatives,
      correctAlternativeIds,
      subtype: subtypeId,
      modelAnswer,
      keyTerms,
    });

    setReviewData({
      request,
      current: {
        label: values.label ?? '',
        information: values.information ?? '',
        modelAnswer,
        keyTerms,
        alternatives: currentAlternatives,
        isChoiceType,
        hasModelAnswer,
        hasKeyTerms,
      },
    });
    setIsReviewOpen(true);
  }

  return (
    <div className="flex flex-col gap-xs">
      {/* Card de configurações */}
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
        <div className="flex items-center gap-nano border-b-hairline border-ctx-layout-border px-mini py-nano">
          <Cog6ToothIcon className="size-xxxs text-ctx-content-title" />
          <span className="text-ctx-content-title ts-subtitle-xs">{t('edit.settings.title')}</span>
        </div>

        <div className="px-mini pb-mini pt-nano">
          <TabMenu className="w-full" defaultValue="general">
            <TabMenuList className="w-full">
              <TabMenuTrigger className="w-full" value="general">
                {t('edit.settings.tabs.general')}
              </TabMenuTrigger>
              <TabMenuTrigger className="w-full" value="dependency">
                {t('edit.settings.tabs.dependency')}
              </TabMenuTrigger>
            </TabMenuList>

            <TabMenuContent value="general">
              <GeneralTab state={state} />
            </TabMenuContent>

            <TabMenuContent value="dependency">
              <DependencyTab state={state} />
            </TabMenuContent>
          </TabMenu>
        </div>
      </div>

      {/* Pré-visualização ao vivo */}
      {componentTypeId > 0 && (
        <QuestionPreviewPanel
          text={currentLabel}
          alternatives={alternatives}
          correctIds={correctAlternativeIds}
          componentType={componentTypeId}
        />
      )}

      {/* Metadados de atualização */}
      {question.updatedBy && (
        <div className="flex justify-end">
          <div className="flex flex-col items-end gap-quark">
            <p className="text-ctx-content-placeholder ts-caption-xxs">
              <strong className="text-ctx-content-base">{t('edit.metadata.updatedBy')}</strong>{' '}
              {question.updatedBy ?? '-'}
            </p>
            <p className="text-ctx-content-placeholder ts-caption-xxs">
              <strong className="text-ctx-content-base">{t('edit.metadata.updatedAt')}</strong>{' '}
              {dayjs(question.updatedAt).format('DD/MM/YYYY - HH:mm:ss') ?? '-'}
            </p>
          </div>
        </div>
      )}

      {/* Barra de ações fixa */}
      <div className="sticky bottom-none">
        <div className="flex items-center justify-between gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-micro">
          <button
            type="button"
            onClick={handleOpenReview}
            disabled={!hasLabelContent}
            className="inline-flex items-center gap-nano whitespace-nowrap rounded-sm bg-ctx-highlight-promote px-mini py-micro text-base-neutral-white ts-subtitle-xxs transition-all hover:bg-base-accent-400 disabled:cursor-not-allowed disabled:opacity-medium"
          >
            <SparklesIcon className="size-xxs" />
            {t('aiReview.button')}
          </button>

          <div className="flex gap-nano">
            <Button hierarchy="secondary" size="md" onClick={onCancel} disabled={isSaving}>
              {t('edit.cancel')}
            </Button>
            <Button hierarchy="primary" size="md" isLoading={isSaving} onClick={handleSave}>
              {t('edit.save')}
            </Button>
          </div>
        </div>
      </div>

      {reviewData && (
        <QuestionAiReviewModal
          open={isReviewOpen}
          onOpenChange={setIsReviewOpen}
          request={reviewData.request}
          current={reviewData.current}
          onApply={applyAiReview}
        />
      )}
    </div>
  );
}
